import type { ComponentPropsWithoutRef, ComponentType } from 'react';
import { cx } from '../../../utils/cx';
import { Facebook, Instagram, TikTok, YouTube } from '../../../icons';

type FooterNavigationLinkComponentProps = Omit<
  ComponentPropsWithoutRef<'a'>,
  'href'
> & { href: string };

export type FooterNavigationLinkComponent =
  ComponentType<FooterNavigationLinkComponentProps>;

export type FooterNavLink = {
  label: string;
  href?: string;
  target?: ComponentPropsWithoutRef<'a'>['target'];
  rel?: ComponentPropsWithoutRef<'a'>['rel'];
  shouldHide?: () => boolean;
};

export type FooterLogo = {
  src: string;
  alt?: string;
  href?: string;
};

export type AppStoreLink = {
  type: 'app-store' | 'google-play';
  href: string;
  imageSrc?: string;
};

export type SocialLink = {
  type: 'tiktok' | 'instagram' | 'youtube' | 'facebook';
  href: string;
  label?: string;
};

export type LegalLink = {
  label: string;
  href?: string;
};

export interface FooterNavigationProps {
  navLinks?: FooterNavLink[];
  logo?: FooterLogo | null;
  copyright?: string;
  className?: string;
  showDivider?: boolean;
  appStoreLinks?: AppStoreLink[];
  socialLinks?: SocialLink[];
  legalLinks?: LegalLink[];
  getAppLabel?: string;
  LinkComponent?: FooterNavigationLinkComponent;
}

const DEFAULT_NAV_LINKS: FooterNavLink[] = [
  {
    label: 'Volunteers',
    href: 'https://goodneighbordayamerica.com/volunteer',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  {
    label: 'Partners',
    href: 'https://goodneighbordayamerica.com/partner',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  {
    label: 'Sponsors',
    href: 'https://goodneighbordayamerica.com/sponsor',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  {
    label: 'FAQs',
    href: 'https://goodneighbordayamerica.com/help',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  { label: 'Contact Us', href: 'mailto:help@goodneighbordayamerica.com' },
  { label: 'Support', href: 'mailto:support@goodneighbordayamerica.com' }
];

const DEFAULT_SOCIAL_LINKS: SocialLink[] = [
  {
    type: 'tiktok',
    href: 'https://www.tiktok.com/@goodneighbordayamerica',
    label: 'TikTok'
  },
  {
    type: 'instagram',
    href: 'https://www.instagram.com/goodneighbordayamerica',
    label: 'Instagram'
  },
  {
    type: 'youtube',
    href: 'https://www.youtube.com/@goodneighbordayamerica',
    label: 'YouTube'
  },
  {
    type: 'facebook',
    href: 'https://www.facebook.com/goodneighbordayamerica',
    label: 'Facebook'
  }
];

const DEFAULT_LEGAL_LINKS: LegalLink[] = [
  {
    label: 'Privacy Policy',
    href: 'https://www.goodneighbordays.org/privacy-policy'
  },
  {
    label: 'Terms of Service',
    href: 'https://www.goodneighbordays.org/terms-of-service'
  }
];

const DEFAULT_APP_STORE_LINKS: AppStoreLink[] = [];

const DEFAULT_LOGO: FooterLogo = {
  src: '/icons/gnda-logo_dark.svg',
  alt: 'Good Neighbor Day America',
  href: '/'
};

const DEFAULT_COPYRIGHT = `© ${new Date().getFullYear()} Good Neighbor. All rights reserved.`;

const SocialIcon = ({
  type,
  className
}: {
  type: SocialLink['type'];
  className?: string;
}) => {
  const iconProps = { className: cx('h-5 w-5', className) };

  switch (type) {
    case 'tiktok':
      return <TikTok {...iconProps} />;
    case 'instagram':
      return <Instagram {...iconProps} />;
    case 'youtube':
      return <YouTube {...iconProps} />;
    case 'facebook':
      return <Facebook {...iconProps} />;
    default:
      return null;
  }
};

export const FooterNavigation = ({
  navLinks = DEFAULT_NAV_LINKS,
  logo = DEFAULT_LOGO,
  copyright = DEFAULT_COPYRIGHT,
  className,
  showDivider = true,
  appStoreLinks = DEFAULT_APP_STORE_LINKS,
  socialLinks = DEFAULT_SOCIAL_LINKS,
  legalLinks = DEFAULT_LEGAL_LINKS,
  getAppLabel = 'Get the app',
  LinkComponent
}: FooterNavigationProps) => {
  const visibleNavLinks = navLinks.filter((link) => !link.shouldHide?.());
  const LinkElement = LinkComponent || 'a';

  return (
    <footer
      className={cx(
        'w-full bg-primary',
        showDivider && 'border-t border-secondary',
        className
      )}
    >
      <div className="mx-auto w-full max-w-container px-4 md:px-8">
        <div className="flex flex-col gap-6 py-10 md:py-16">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            {logo?.src && (
              <div className="flex-shrink-0">
                <a
                  href={logo.href || '/'}
                  className="inline-flex rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
                  aria-label={logo.alt || 'Good Neighbor home'}
                >
                  <img
                    src={logo.src}
                    alt={logo.alt || ''}
                    className="h-12 w-auto"
                  />
                </a>
              </div>
            )}
            {appStoreLinks.length > 0 && (
              <div className="flex flex-col items-start gap-4">
                <span className="text-md font-medium text-primary">
                  {getAppLabel}
                </span>
                <div className="flex gap-3">
                  {appStoreLinks.map((appLink) => (
                    <a
                      key={appLink.type}
                      href={appLink.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex rounded-md outline-focus-ring transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
                      aria-label={
                        appLink.type === 'app-store'
                          ? 'Download on the App Store'
                          : 'Get it on Google Play'
                      }
                    >
                      {appLink.imageSrc ? (
                        <img
                          src={appLink.imageSrc}
                          alt={
                            appLink.type === 'app-store'
                              ? 'Download on the App Store'
                              : 'Get it on Google Play'
                          }
                          className="h-10 w-auto"
                        />
                      ) : (
                        <AppStoreBadge type={appLink.type} />
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <nav aria-label="Footer navigation">
            <ul className="flex flex-wrap gap-x-6 gap-y-3 md:gap-x-8">
              {visibleNavLinks.map((link) => {
                const target =
                  link.target ||
                  (link.href?.startsWith('http') ? '_blank' : undefined);
                const rel =
                  link.rel || (target === '_blank' ? 'noreferrer' : undefined);

                return (
                  <li key={link.label}>
                    {link.href ? (
                      <LinkElement
                        className="text-md font-semibold text-tertiary transition-colors hover:text-secondary_hover"
                        href={link.href}
                        target={target}
                        rel={rel}
                      >
                        {link.label}
                      </LinkElement>
                    ) : (
                      <span className="text-md font-semibold text-tertiary">
                        {link.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
        <div className="flex flex-col gap-4 border-t border-secondary pt-6 pb-8 md:flex-row md:items-center md:justify-between md:gap-6 md:pt-8 md:pb-12">
          <div className="flex flex-col flex-wrap gap-8 md:flex-row text-md text-quaternary">
            <span>{copyright}</span>
            <span>
              {legalLinks.length > 0 && (
                <div className="flex gap-x-8">
                  {legalLinks.map((link) =>
                    link.href ? (
                      <LinkElement
                        key={link.label}
                        href={link.href}
                        className="text-md font-normal text-quaternary underline transition-colors hover:text-secondary_hover"
                      >
                        {link.label}
                      </LinkElement>
                    ) : (
                      <span
                        key={link.label}
                        className="text-md font-normal text-quaternary underline"
                      >
                        {link.label}
                      </span>
                    )
                  )}
                </div>
              )}
            </span>
          </div>
          {socialLinks.length > 0 && (
            <div className="flex gap-4 order-first md:order-last">
              {socialLinks.map((social) => (
                <a
                  key={social.type}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xs text-fg-quaternary transition-colors hover:text-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                  aria-label={social.label || social.type}
                >
                  <SocialIcon type={social.type} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

const AppStoreBadge = ({ type }: { type: 'app-store' | 'google-play' }) => {
  if (type === 'app-store') {
    return (
      <div className="flex h-10 items-center gap-2 rounded-md bg-black px-3 py-2 text-white">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-[8px] leading-tight">Download on the</span>
          <span className="text-sm font-semibold leading-tight">App Store</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-10 items-center gap-2 rounded-md bg-black px-3 py-2 text-white">
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
      </svg>
      <div className="flex flex-col">
        <span className="text-[8px] leading-tight">GET IT ON</span>
        <span className="text-sm font-semibold leading-tight">Google Play</span>
      </div>
    </div>
  );
};

FooterNavigation.displayName = 'FooterNavigation';
